import { useState } from 'react'

import {
  Button,
  Group,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconArrowLeft, IconBuilding } from '@tabler/icons-react'
import { Link, useRouter } from '@tanstack/react-router'

import { useCreateFund } from '@repo/common/queries/admin/fund'

import { TowerCard } from '@/components/ui/TowerCard'

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface AddFundPageProps {}

export const AddFundPage: React.FC<AddFundPageProps> = () => {
  const router = useRouter()
  const [fundName, setFundName] = useState('')
  const [startingSharePrice, setStartingSharePrice] = useState<number | string>(
    1.0,
  )
  const [managementFeePercentage, setManagementFeePercentage] = useState<
    number | string
  >(0)
  const [tokenSymbol, setTokenSymbol] = useState('')
  const [published, setPublished] = useState(false)
  const [publicationSchedule, setPublicationSchedule] = useState(
    '12pm daily, weekly on wed official',
  )
  const [formError, setFormError] = useState<string | null>(null)

  const createFundMutation = useCreateFund()

  const handleSubmit = () => {
    setFormError(null)

    if (!fundName.trim()) {
      setFormError('Fund name is required')
      return
    }

    if (!tokenSymbol.trim()) {
      setFormError('Token symbol is required')
      return
    }

    if (!publicationSchedule.trim()) {
      setFormError('Publication schedule is required')
      return
    }

    if (typeof startingSharePrice !== 'number' || startingSharePrice <= 0) {
      setFormError('Starting share price must be a positive number')
      return
    }

    if (
      typeof managementFeePercentage !== 'number' ||
      managementFeePercentage < 0
    ) {
      setFormError('Management fee percentage must be a non-negative number')
      return
    }

    createFundMutation.mutate(
      {
        name: fundName.trim(),
        starting_share_price: startingSharePrice,
        management_fee_percentage: managementFeePercentage,
        token_symbol: tokenSymbol.trim().toUpperCase(),
        published,
        publication_schedule: publicationSchedule.trim(),
      },
      {
        onSuccess: () => {
          void router.navigate({ to: '/funds' })
        },
        onError: (error) => {
          setFormError(error.message)
        },
      },
    )
  }

  const isFormValid =
    fundName.trim() &&
    tokenSymbol.trim() &&
    publicationSchedule.trim() &&
    typeof startingSharePrice === 'number' &&
    startingSharePrice > 0 &&
    typeof managementFeePercentage === 'number' &&
    managementFeePercentage >= 0

  const isSubmitting = createFundMutation.isPending

  return (
    <TowerCard
      title="Add New Fund"
      description="Create a new fund with initial configuration"
      icon={<IconBuilding size={24} />}
    >
      <Stack gap="lg">
        <Group>
          <Button
            component={Link}
            to="/funds"
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
          >
            Back to Funds
          </Button>
        </Group>

        <Paper p="md" withBorder>
          <Stack gap="md">
            <Title order={3}>Fund Details</Title>

            <TextInput
              label="Fund Name"
              placeholder="Enter fund name"
              value={fundName}
              onChange={(event) => {
                setFundName(event.currentTarget.value)
              }}
              required
              description="The display name for this fund"
            />

            <NumberInput
              label="Starting Share Price"
              value={startingSharePrice}
              onChange={setStartingSharePrice}
              required
              decimalScale={6}
              min={0.000001}
              prefix="$"
              description="Initial price per share in USD"
            />

            <NumberInput
              label="Management Fee Percentage"
              value={managementFeePercentage}
              onChange={setManagementFeePercentage}
              required
              decimalScale={2}
              min={0}
              max={100}
              suffix="%"
              description="Annual management fee as a percentage"
            />

            <TextInput
              label="Token Symbol"
              placeholder="e.g., FUND"
              value={tokenSymbol}
              onChange={(event) => {
                setTokenSymbol(event.currentTarget.value.toUpperCase())
              }}
              required
              description="Trading symbol for this fund token"
              maxLength={10}
            />

            <TextInput
              label="Publication Schedule"
              value={publicationSchedule}
              onChange={(event) => {
                setPublicationSchedule(event.currentTarget.value)
              }}
              required
              description="Schedule for fund NAV publications"
              placeholder="12pm daily, weekly on wed official"
            />

            <Switch
              label="Published"
              description="Make this fund visible to public users"
              checked={published}
              onChange={(event) => {
                setPublished(event.currentTarget.checked)
              }}
            />

            {formError && (
              <Text c="red" size="sm">
                {formError}
              </Text>
            )}

            <Group justify="flex-end">
              <Button variant="outline" component={Link} to="/funds">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
                loading={isSubmitting}
              >
                Create Fund
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </TowerCard>
  )
}
